import { Injectable, OnDestroy } from "@angular/core";
import { AngularFireMessaging } from "@angular/fire/compat/messaging";
import { MqttService } from "ngx-mqtt";
import { filter, interval, map, merge, Subject, takeUntil } from "rxjs";
import { GtmService } from "./gtm-service/gtm.service";
import { MqttEventService } from "./mqtt-event.service";
import { GlobalService } from "./global.service";

@Injectable({
    providedIn: "root",
})
export class RealtimeEventService implements OnDestroy {
    /* =====================================================
       STREAMS
       ===================================================== */
    private destroy$ = new Subject<void>();
    private realtimeEventSubject = new Subject<any>();
    public realtimeEvent$ = this.realtimeEventSubject.asObservable();

    /* =====================================================
       MULTI TAB
       ===================================================== */
    private channel = new BroadcastChannel("mqtt-events");

    private callInitializeListeners = false;

    constructor(
        private mqttService: MqttService,
        private mqttEventService: MqttEventService,
        private afMessaging: AngularFireMessaging,
        private gtmService: GtmService,
        private globalService: GlobalService
    ) {}

    /* =====================================================
       INIT
       ===================================================== */
    initializeListeners() {
        if (this.callInitializeListeners) return;
        this.callInitializeListeners = true;

        /* ---------- IS LEADER TAB ---------- */
        const rawLeader = localStorage.getItem(this.mqttEventService.LEADER_KEY);
        const leader = rawLeader ? JSON.parse(rawLeader) : null;
        const tabId = sessionStorage.getItem(this.mqttEventService.TAB_ID_KEY);
        const isLeaderTab = leader.tabId === tabId;
        console.log("::isLeaderTab", isLeaderTab);

        /* ---------- WINDOW MQTT (FOLLOWERS) ---------- */
        const windowMqtt$ = new Subject<any>();

        if (!isLeaderTab) {
            this.channel.onmessage = (event) => {
                console.log(":::event", event);

                if (event.data?.source === "MQTT") {
                    console.log("MQTT from leader tab");
                    windowMqtt$.next(event.data.payload);
                }
            };
        }

        /* ---------- DIRECT MQTT (LEADER ONLY) ---------- */
        console.log("::isLeaderTab", isLeaderTab);

        const directMqtt$ = isLeaderTab
            ? this.mqttService.onMessage.pipe(
                  map((packet: any) => {
                      const payload = JSON.parse(packet.payload.toString());
                      console.log(":::::payload", payload);

                      // 🔥 broadcast to other tabs
                      this.channel.postMessage({
                          source: "MQTT",
                          payload,
                      });

                      return payload;
                  })
              )
            : new Subject<any>(); // followers get empty stream

        /* ---------- FCM (ALL TABS) ---------- */
        const afMessaging$ = this.afMessaging.messages.pipe(
            map((packet: any) => {
                let data = packet.data;
                if (data?.data) {
                    data.data = JSON.parse(data.data);
                }
                this.gtmService.processRealTimeEvents(data);
                return data;
            })
        );

        /* ---------- MERGE ALL ---------- */
        const combinedStream = merge(
            windowMqtt$.asObservable(), // follower tabs
            directMqtt$, // leader tab
            afMessaging$ // all tabs
        );

        /* ---------- DEDUP ---------- */
        const processedKeys = new Set<string>();
        interval(30000).subscribe(() => processedKeys.clear());

        combinedStream
            .pipe(
                filter((data: any) => !!data?.randomKey),
                filter((data: any) => {
                    if (processedKeys.has(data.randomKey)) return false;
                    processedKeys.add(data.randomKey);
                    return true;
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((data) => this.handleEvent(data));
    }

    /* =====================================================
       HANDLER
       ===================================================== */
    private handleEvent(data: any) {
        console.log("Realtime Event:", data);
        this.realtimeEventSubject.next(data);
    }

    /* =====================================================
       CLEANUP
       ===================================================== */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.channel.close();
    }
}
